/**
 * TanStack Query hook for theme_preferences in Supabase.
 * Loads and persists theme settings (color theme, glow, background type, etc.) per user.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { ColorTheme, GlowIntensity, BackgroundType } from "@/providers/ThemeProvider";
import { logger } from "@/lib/logger";

export type Theme = "light" | "dark" | "system";

export interface ThemePreferencesRow {
  id: string;
  user_id: string;
  theme: Theme;
  color_theme: ColorTheme;
  glow_intensity: GlowIntensity;
  performance_mode: boolean;
  background_type: BackgroundType;
  background_images: string[];
  created_at?: string;
  updated_at?: string;
}

export const themePreferencesKeys = {
  all: ["theme-preferences"] as const,
  user: (userId: string) => [...themePreferencesKeys.all, userId] as const,
};

const STALE_TIME = 2 * 60 * 1000; // 2 minutes

export function useThemePreferencesQuery() {
  const { user } = useAuth();
  return useQuery({
    queryKey: themePreferencesKeys.user(user?.id ?? ""),
    queryFn: async (): Promise<ThemePreferencesRow | null> => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("theme_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        ...data,
        background_images: Array.isArray(data.background_images) ? data.background_images : [],
      } as ThemePreferencesRow;
    },
    enabled: !!user?.id,
    staleTime: STALE_TIME,
  });
}

export function useThemePreferencesMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (prefs: Omit<ThemePreferencesRow, "id" | "user_id" | "created_at" | "updated_at">) => {
      if (!user?.id) throw new Error("Not authenticated");
      const row = {
        user_id: user.id,
        theme: prefs.theme,
        color_theme: prefs.color_theme,
        glow_intensity: prefs.glow_intensity,
        performance_mode: prefs.performance_mode,
        background_type: prefs.background_type,
        background_images: prefs.background_images ?? [],
      };
      const { error } = await supabase.from("theme_preferences").upsert(row, {
        onConflict: "user_id",
      });
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      if (user?.id) {
        queryClient.setQueryData(themePreferencesKeys.user(user.id), (old: ThemePreferencesRow | null) =>
          old ? { ...old, ...variables } : null
        );
      }
    },
    onError: (err) => logger.error("Theme preferences persist failed", { error: err }),
  });
}
